import Accordion, { AccordionItem } from '@service-victoria/component-library/components/Accordion';
import Body from '@service-victoria/component-library/components/Text/Body';
import Heading from '@service-victoria/component-library/components/Text/Heading';
import Link from '@service-victoria/component-library/components/Text/Link';
import { Spacer } from '@service-victoria/component-library/utils/styled';
import React from 'react';

import LinkButton from 'Components/LinkButton';
import Page from 'Components/Page';
import { externalLinks } from 'utils/constants';

import * as Styled from './styled';

const BRP_LOGIN_URL = 'https://busreg.covid19.dhhs.vic.gov.au/s/login/';
const CHECKIN_URL = process.env.REACT_APP_BASENAME ?? '/';

const accordionItems: AccordionItem[] = [
  {
    title: 'Where do I find the Kiosk check-in service?',
    component: (
      <>
        <Link href={CHECKIN_URL} target='_blank'>
          Kiosk check-in service
        </Link>
        <Body>
          For Kiosk to work best, you need to open a normal browser tab. If you use a private or
          ‘incognito’ browser window, you&apos;ll need to re-log in to your location each time you
          want to use Kiosk check-in.
        </Body>
      </>
    )
  },
  {
    title: 'What devices can I use?',
    component: (
      <>
        <Body>
          As long as you have an internet connection and web browser, you can connect using:
        </Body>
        <Styled.List>
          <Styled.ListItem>phones</Styled.ListItem>
          <Styled.ListItem>tablets</Styled.ListItem>
          <Styled.ListItem>laptops</Styled.ListItem>
          <Styled.ListItem>computers</Styled.ListItem>
        </Styled.List>
      </>
    )
  },
  {
    title: 'Do I need to enter patron details or can they do it themselves?',
    component: (
      <Body>
        We made the Kiosk check-in service so you can help those patrons who can&apos;t check-in
        using the QR code and app. Patrons who can check themselves in with the QR code and app
        should still do so.
      </Body>
    )
  },
  {
    title: 'Can I enter multiple patrons at once?',
    component: (
      <Body>
        Not yet. But you&apos;ll be able to in the next update of this service, coming soon.
      </Body>
    )
  },
  {
    title: 'Can I re-enter my venue details or move my device to a new location?',
    component: (
      <Body>
        Yes. Tap the &apos;update&apos; icon at the top of the screen when you&apos;re on the Kiosk
        check-in service.  
      </Body>
    )
  },
  {
    title: 'What if I only have one name?',
    component: (
      <>
        <Body>
          Kiosk check-in knows the time and date, you only have to enter the mobile or landline
          number and the names.
        </Body>
        <Body>If you have only one name, enter it for both Given name and Family name.</Body>
      </>
    )
  },
  {
    title: 'How do I bookmark the Kiosk check-in service?',
    component: (
      <>
        <Body fontWeight='bold'>In Chrome:</Body>
        <Styled.List as='ol'>
          <Styled.ListItem>
            Navigate to{' '}
            <Link href={CHECKIN_URL} target='_blank'>
              www.service.vic.gov.au/business/kiosk-check-in
            </Link>
          </Styled.ListItem>
          <Styled.ListItem>
            Click the star icon on the right side of the address bar.
          </Styled.ListItem>
          <Styled.ListItem>Name the bookmark.</Styled.ListItem>
          <Styled.ListItem>Select the folder you want the webpage to be saved to.</Styled.ListItem>
          <Styled.ListItem>Click “Done” to bookmark the webpage.</Styled.ListItem>
        </Styled.List>
        <Body fontWeight='bold'>Other browsers:</Body>
        <Styled.List>
          <Styled.ListItem>
            <Link href={externalLinks.safariBookmarkHelp} target='_blank' showExtraLink>
              Safari
            </Link>
          </Styled.ListItem>
          <Styled.ListItem>
            <Link href={externalLinks.firefoxBookmarkHelp} target='_blank' showExtraLink>
              Firefox
            </Link>
          </Styled.ListItem>
          <Styled.ListItem>
            <Link href={externalLinks.ieBookmarkHelp} target='_blank' showExtraLink>
              Internet Explorer
            </Link>
          </Styled.ListItem>
          <Styled.ListItem>
            <Link href={externalLinks.edgeBookmarkHelp} target='_blank' showExtraLink>
              Microsoft Edge
            </Link>
          </Styled.ListItem>
        </Styled.List>
      </>
    )
  },
  {
    title: 'Can I use private browsing or an ‘incognito’ window for Kiosk check-in?',
    component: (
      <Body>
        For Kiosk check-in to work best, you need to open a normal browser tab.
        <br />
        If you use a private or ‘incognito’ browser window, you&apos;ll need to re-log in to your
        location each time you use Kiosk check-in.
      </Body>
    )
  },
  {
    title: "What if I'm not connected to the internet?",
    component: (
      <Body>
        You can&apos;t use the Kiosk check-in service if you can&apos;t connect to the internet.
        <br />
        If there&apos;s no connection, patrons must check in with the QR code and Service Victoria
        app.
        <br />
        If neither option works because of network problems, you have to record the usual details -
        Given name, Family name and at least one phone number - manually.
      </Body>
    )
  }
].map((item, index) => ({ ...item, id: `${index}` }));

const About: React.FC = () => {
  return (
    <Page
      showHeader={true}
      contentSize={'default'}
      showContentFooter={false}
      showPageFooter={true}
      breadCrumbOptions={[
        { text: 'Business', link: 'https://service.vic.gov.au/business' },
        { text: 'Kiosk check-in', link: '' }
      ]}>
      <Styled.Section>
        <Styled.H1>Kiosk check-in</Styled.H1>
        <Spacer.Large>
          <Body>
            From 28 May, venues and businesses must use the Victorian Government QR Code Service
            through the Service Victoria app for customer check-in.
          </Body>
          <Body>Your customers should use their own device to self-check-in if they can.</Body>
          <Body>
            But if they can&apos;t, you&apos;ll have to help them if you&apos;re required to keep
            records.
          </Body>
          <Body>
            You can check guests in without using the app at all by using the Kiosk check-in
            service.
          </Body>
        </Spacer.Large>

        <Spacer.Large>
          <Heading.H2 fontWeight={600}>Setting up Kiosk check-in</Heading.H2>
          <Body fontWeight={600}>To set up your venue areas and main Kiosk check-in contact</Body>
          <Styled.List as='ol'>
            <Styled.ListItem>
              Log in to the{' '}
              <Link href={BRP_LOGIN_URL} target='_blank'>
                Victorian Government QR Code Service
              </Link>{' '}
              if you&apos;re already registered.
            </Styled.ListItem>
            <Styled.ListItem>Choose the location or area within your venue.</Styled.ListItem>
            <Styled.ListItem>
              Enter the contact details of the person who will be your main Kiosk check-in contact.
            </Styled.ListItem>
          </Styled.List>
          <Body fontWeight={600}>
            For each device and main Kiosk contact in a defined zone or area at your venue
          </Body>
          <Styled.List as='ol'>
            <Styled.ListItem>
              Make sure device(s) used for check-in are connected to the internet.
            </Styled.ListItem>
            <Styled.ListItem>
              Navigate to{' '}
              <Link href={CHECKIN_URL} target='_blank'>
                Kiosk check-in
              </Link>{' '}
              from your device.
            </Styled.ListItem>
            <Styled.ListItem>
              Enter the Kiosk check-in code we emailed to the Kiosk check-in contact. Check junk and
              spam folders, just in case.
            </Styled.ListItem>
            <Styled.ListItem>
              Enter the secure One-Time-Password (OTP) we also sent to the contact email, then
              choose &apos;Next&apos;.
            </Styled.ListItem>
          </Styled.List>
          <Body>
            You&apos;re now ready to check in patrons using the web form from your device.
          </Body>
          <Body>
            For more info, check the FAQ below or call Business Victoria on{' '}
            <Link href='tel:132215'>13 22 15</Link>.
          </Body>

          <Styled.CallToActionContainer>
            <LinkButton type='anchor' href={CHECKIN_URL} target='_blank'>
              Go to Kiosk check-in
            </LinkButton>
          </Styled.CallToActionContainer>
        </Spacer.Large>

        <Spacer.Large>
          <Heading.H2 fontWeight={600}>Who you have to help check-in</Heading.H2>
          <Body>
            Check-in at venues and businesses helps with contact tracing if there&apos;s a COVID
            outbreak.
          </Body>
          <Body>
            Some customers can&apos;t check themselves in with the Service Victoria app because
            they:
          </Body>
          <Styled.List>
            <Styled.ListItem>don&apos;t have a device with them</Styled.ListItem>
            <Styled.ListItem>have an older device that can&apos;t run apps</Styled.ListItem>
            <Styled.ListItem>don&apos;t use mobile devices at all</Styled.ListItem>
            <Styled.ListItem>aren&apos;t confident with technology</Styled.ListItem>
            <Styled.ListItem>aren&apos;t confident readers</Styled.ListItem>
          </Styled.List>
          <Body>
            If you have record-keeping obligations under the current Department of Health
            directions, you must help check-in people who can&apos;t do it alone, using the Kiosk
            check-in service.
          </Body>
        </Spacer.Large>

        <Spacer.Large>
          <Heading.H2 fontWeight={600}>How Kiosk check-in works</Heading.H2>
          <Body>Kiosk check-in is a simple online service.</Body>
          <Body>
            Your staff can navigate there from your chosen device and enter the customer&apos;s:
          </Body>
          <Styled.List>
            <Styled.ListItem>Given and Family names</Styled.ListItem>
            <Styled.ListItem>contact number (mobile preferred, landline okay)</Styled.ListItem>
          </Styled.List>
          <Body>
            Kiosk check-in is linked to a defined location or zone at your venue. You set this up on
            the{' '}
            <Link href={BRP_LOGIN_URL} target='_blank'>
              Victorian Government QR Code Service
            </Link>
            .
          </Body>
        </Spacer.Large>
      </Styled.Section>

      <Styled.Section>
        <Styled.H1>FAQ</Styled.H1>
        <Accordion items={accordionItems} />
      </Styled.Section>
    </Page>
  );
};

export default About;
